import type { LeadId } from "./crm";

export type CrmTagId = string & { readonly __brand: "CrmTagId" };
export type LeadTagAssignmentId = string & {
  readonly __brand: "LeadTagAssignmentId";
};
export type CrmTaskId = string & { readonly __brand: "CrmTaskId" };
export type TaskReminderId = string & {
  readonly __brand: "TaskReminderId";
};
export type LeadNoteId = string & { readonly __brand: "LeadNoteId" };
export type CrmEngagementRevision = number & {
  readonly __brand: "CrmEngagementRevision";
};
export type AdminLeadEngagementCursor = string & {
  readonly __brand: "AdminLeadEngagementCursor";
};

export const crmTagStatuses = ["ACTIVE", "ARCHIVED"] as const;
export type CrmTagStatus = (typeof crmTagStatuses)[number];

export const leadTagAssignmentStatuses = ["ACTIVE", "REMOVED"] as const;
export type LeadTagAssignmentStatus =
  (typeof leadTagAssignmentStatuses)[number];

export const crmTaskStatuses = ["OPEN", "COMPLETED", "CANCELLED"] as const;
export type CrmTaskStatus = (typeof crmTaskStatuses)[number];

export const taskReminderStatuses = ["PENDING", "CANCELLED"] as const;
export type TaskReminderStatus = (typeof taskReminderStatuses)[number];

export const adminLeadEngagementPageSizes = [25] as const;
export type AdminLeadEngagementPageSize =
  (typeof adminLeadEngagementPageSizes)[number];

export const crmTagDisplayLabelMaxLength = 32;
export const crmTaskTitleMaxLength = 120;
export const leadNoteBodyMaxLength = 4000;

export interface CrmTagRecord {
  readonly id: CrmTagId;
  readonly organizationId: string;
  readonly tagKey: string;
  readonly displayLabel: string;
  readonly status: CrmTagStatus;
  readonly revision: CrmEngagementRevision;
  readonly createdByUserId: string;
  readonly createdCorrelationId: string;
  readonly createIdempotencyKeyHash: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly archivedByUserId: string | null;
  readonly archiveCorrelationId: string | null;
  readonly archiveIdempotencyKeyHash: string | null;
  readonly archivedAt: string | null;
}

export interface LeadTagAssignmentRecord {
  readonly id: LeadTagAssignmentId;
  readonly organizationId: string;
  readonly leadId: LeadId;
  readonly tagId: CrmTagId;
  readonly status: LeadTagAssignmentStatus;
  readonly revision: CrmEngagementRevision;
  readonly attachedByUserId: string;
  readonly attachCorrelationId: string;
  readonly attachIdempotencyKeyHash: string;
  readonly attachedAt: string;
  readonly updatedAt: string;
  readonly removedByUserId: string | null;
  readonly removeCorrelationId: string | null;
  readonly removeIdempotencyKeyHash: string | null;
  readonly removedAt: string | null;
}

export interface CrmTaskRecord {
  readonly id: CrmTaskId;
  readonly organizationId: string;
  readonly leadId: LeadId;
  readonly title: string;
  readonly dueAt: string;
  readonly status: CrmTaskStatus;
  readonly revision: CrmEngagementRevision;
  readonly createdByUserId: string;
  readonly createdCorrelationId: string;
  readonly createIdempotencyKeyHash: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly closedByUserId: string | null;
  readonly closeCorrelationId: string | null;
  readonly closeIdempotencyKeyHash: string | null;
  readonly closedAt: string | null;
}

export interface TaskReminderRecord {
  readonly id: TaskReminderId;
  readonly organizationId: string;
  readonly taskId: CrmTaskId;
  readonly scheduledFor: string;
  readonly status: TaskReminderStatus;
  readonly revision: CrmEngagementRevision;
  readonly createdByUserId: string;
  readonly createdCorrelationId: string;
  readonly createIdempotencyKeyHash: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly cancelledByUserId: string | null;
  readonly cancelCorrelationId: string | null;
  readonly cancelIdempotencyKeyHash: string | null;
  readonly cancelledAt: string | null;
}

export interface LeadNoteRecord {
  readonly id: LeadNoteId;
  readonly organizationId: string;
  readonly leadId: LeadId;
  readonly bodyText: string;
  readonly supersedesNoteId: LeadNoteId | null;
  readonly createdByUserId: string;
  readonly correlationId: string;
  readonly idempotencyKeyHash: string;
  readonly createdAt: string;
}

export interface AdminLeadEngagementPageQuery {
  readonly leadId: LeadId;
  readonly cursor: AdminLeadEngagementCursor | null;
  readonly limit: AdminLeadEngagementPageSize;
}

export interface AdminLeadTag {
  readonly assignmentId: LeadTagAssignmentId;
  readonly leadId: LeadId;
  readonly tagId: CrmTagId;
  readonly displayLabel: string;
  readonly revision: CrmEngagementRevision;
  readonly attachedAt: string;
  readonly updatedAt: string;
}

export interface AdminLeadTagPage {
  readonly leadId: LeadId;
  readonly items: readonly AdminLeadTag[];
  readonly nextCursor: AdminLeadEngagementCursor | null;
}

export interface AdminLeadTask {
  readonly taskId: CrmTaskId;
  readonly leadId: LeadId;
  readonly title: string;
  readonly dueAt: string;
  readonly status: CrmTaskStatus;
  readonly revision: CrmEngagementRevision;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface AdminLeadTaskPage {
  readonly leadId: LeadId;
  readonly items: readonly AdminLeadTask[];
  readonly nextCursor: AdminLeadEngagementCursor | null;
}

export interface AdminLeadTaskReminder {
  readonly reminderId: TaskReminderId;
  readonly leadId: LeadId;
  readonly taskId: CrmTaskId;
  readonly scheduledFor: string;
  readonly status: TaskReminderStatus;
  readonly revision: CrmEngagementRevision;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface AdminLeadTaskReminderPage {
  readonly leadId: LeadId;
  readonly items: readonly AdminLeadTaskReminder[];
  readonly nextCursor: AdminLeadEngagementCursor | null;
}

export interface AdminLeadNote {
  readonly noteId: LeadNoteId;
  readonly leadId: LeadId;
  readonly bodyText: string;
  readonly supersedesNoteId: LeadNoteId | null;
  readonly createdAt: string;
}

export interface AdminLeadNotePage {
  readonly leadId: LeadId;
  readonly items: readonly AdminLeadNote[];
  readonly nextCursor: AdminLeadEngagementCursor | null;
}

export type AdminLeadTagMutationRequest =
  | {
      readonly action: "ADD";
      readonly leadId: LeadId;
      readonly displayLabel: string;
    }
  | {
      readonly action: "REMOVE";
      readonly leadId: LeadId;
      readonly assignmentId: LeadTagAssignmentId;
      readonly expectedRevision: CrmEngagementRevision;
      readonly expectedUpdatedAt: string;
    };

export type AdminLeadTagMutationOutcome =
  | {
      readonly kind: "confirmed";
      readonly action: "ADDED";
      readonly item: AdminLeadTag;
    }
  | {
      readonly kind: "confirmed";
      readonly action: "REMOVED";
      readonly assignmentId: LeadTagAssignmentId;
      readonly revision: CrmEngagementRevision;
      readonly updatedAt: string;
    }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };

export type AdminLeadTaskMutationRequest =
  | {
      readonly action: "CREATE";
      readonly leadId: LeadId;
      readonly title: string;
      readonly dueAt: string;
    }
  | {
      readonly action: "COMPLETE" | "CANCEL";
      readonly leadId: LeadId;
      readonly taskId: CrmTaskId;
      readonly expectedRevision: CrmEngagementRevision;
      readonly expectedUpdatedAt: string;
    };

export type AdminLeadTaskMutationOutcome =
  | {
      readonly kind: "confirmed";
      readonly action: "CREATED" | "COMPLETED" | "CANCELLED";
      readonly item: AdminLeadTask;
    }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };

export type AdminLeadTaskReminderMutationRequest =
  | {
      readonly action: "SCHEDULE";
      readonly leadId: LeadId;
      readonly taskId: CrmTaskId;
      readonly scheduledFor: string;
    }
  | {
      readonly action: "CANCEL";
      readonly leadId: LeadId;
      readonly reminderId: TaskReminderId;
      readonly expectedRevision: CrmEngagementRevision;
      readonly expectedUpdatedAt: string;
    };

export type AdminLeadTaskReminderMutationOutcome =
  | {
      readonly kind: "confirmed";
      readonly action: "SCHEDULED" | "CANCELLED";
      readonly item: AdminLeadTaskReminder;
    }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };

export interface AdminLeadNoteCreateRequest {
  readonly leadId: LeadId;
  readonly bodyText: string;
  readonly supersedesNoteId: LeadNoteId | null;
}

export type AdminLeadNoteCreateOutcome =
  | {
      readonly kind: "confirmed";
      readonly item: AdminLeadNote;
    }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };
